/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Word-Level Visual Diff Viewer Component (Task 10.5.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useMemo } from 'react';
import { computeWordDiff, DiffToken } from '../../utils/diff-helper';

interface DiffViewerProps {
  tmSource: string;
  currentSource: string;
  className?: string;
}

export const DiffViewer: React.FC<DiffViewerProps> = ({
  tmSource,
  currentSource,
  className = '',
}) => {
  const diffTokens: DiffToken[] = useMemo(() => {
    return computeWordDiff(tmSource, currentSource);
  }, [tmSource, currentSource]);

  return (
    <div
      className={`p-2.5 rounded-lg bg-surface border border-surface-border text-xs leading-relaxed font-sans select-text break-words ${className}`}
    >
      {diffTokens.map((token, index) => {
        if (token.type === 'added') {
          return (
            <span
              key={index}
              className="inline-block bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold px-1 py-0.2 mx-0.5 rounded border border-emerald-500/30"
              title="Added in current source"
            >
              {token.value}
            </span>
          );
        }

        if (token.type === 'removed') {
          return (
            <span
              key={index}
              className="inline-block bg-rose-500/15 text-rose-600 dark:text-rose-400 line-through px-1 py-0.2 mx-0.5 rounded border border-rose-500/30 opacity-80"
              title="Removed from TM template"
            >
              {token.value}
            </span>
          );
        }

        return <span key={index}>{token.value}</span>;
      })}
    </div>
  );
};
